import React, { lazy, Suspense, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import Loading from "../components/Loading/index";
import { getCurrentUser } from "../redux/auth/index";
import AuthVerify from "./auth-verify";
import LogActivity from "./logActivity";

// Lazy load components
const Dashboard = lazy(() => import("../components/dashboard/index"));
const SignIn = lazy(() => import("../views/signin/index"));
const AccountVerification = lazy(
  () => import("../views/account-verification/index"),
);
const NotFound = lazy(() => import("../views/404/notfound"));
const ForgotPassword = lazy(() => import("../views/forgot-password"));
const PasswordReset = lazy(() => import("../views/password-reset/index"));
const SignOut = lazy(() => import("../views/signout/index"));
const OtpVerification = lazy(() => import("../views/otp-verification/index"));
const CommonPlans = lazy(() => import("../views/plans/index"));
const SignUp = lazy(() => import("../views/signup-payment/SignUp"));
const Success = lazy(() => import("../views/signup-payment/Success"));

// Component to redirect unauthenticated users to sign-in while preserving intended destination
function NavigateToSignIn() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  // Extract email parameter if present
  const emailParam = searchParams.get("email");

  // Build the sign-in URL with email parameter if it exists
  const signInSearchParams = new URLSearchParams();
  if (emailParam) {
    signInSearchParams.set("email", emailParam);
  }

  // Build the destination path without email parameter (to avoid duplication)
  const destinationSearchParams = new URLSearchParams(location.search);
  destinationSearchParams.delete("email");
  const destinationPath =
    location.pathname +
    (destinationSearchParams.toString()
      ? `?${destinationSearchParams.toString()}`
      : "");

  const signInUrl = `/sign-in${signInSearchParams.toString() ? `?${signInSearchParams.toString()}` : ""}`;

  return <Navigate to={signInUrl} state={{ from: destinationPath }} replace />;
}

function RedirectIfAuthenticatedToDashboard() {
  return <Navigate to="/dashboard" replace />;
}

function RoutesNew() {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getCurrentUser());
  }, [dispatch]);

  const auth = useSelector((state) => state.auth);
  const isAuthenticated = auth?.authenticated || false;

  if (auth.authLoading) return <Loading />;
  return (
    <BrowserRouter>
      <LogActivity />
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route
            path="/sign-up"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <SignUp />
              )
            }
          />
          <Route path="/sign-up/success" element={<Success />} />
          <Route
            path="/sign-in"
            element={
              isAuthenticated ? (
                <RedirectIfAuthenticatedToDashboard />
              ) : (
                <SignIn />
              )
            }
          />
          <Route
            path="/forgot-password"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <ForgotPassword />
              )
            }
          />
          <Route
            path="/password-reset"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <PasswordReset />
              )
            }
          />
          <Route
            path="/reset-password"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <PasswordReset />
              )
            }
          />
          <Route
            path="/"
            element={isAuthenticated ? <Dashboard /> : <SignIn />}
          />
          <Route
            path="/otp-verification"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <OtpVerification />
              )
            }
          />

          <Route
            path="/account-verification"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <AccountVerification />
              )
            }
          />

          <Route path="/sign-out" element={<SignOut />} />

          <Route
            path="/dashboard/*"
            element={
              isAuthenticated ? (
                <>
                  <AuthVerify />
                  <Dashboard />{" "}
                </>
              ) : (
                <NavigateToSignIn />
              )
            }
          />

          <Route path="/available-plans" element={<CommonPlans />} />

          {/* Route the rest paths to 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default RoutesNew;
